import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PlusCircle, Search, ExternalLink, Package, ShoppingCart } from "lucide-react";
import { motion } from "framer-motion";
import api from "../../lib/api";
import { PageHeader, StatusBadge, LoadingRows, EmptyState } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

export default function PartnerWebsites() {
  const [sites, setSites] = useState(null);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const load = useCallback(() => { api.get("/partner/websites", { params: { q } }).then((r) => setSites(r.data)); }, [q]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="partner-websites">
      <PageHeader title="My Websites" subtitle="Every client website you manage.">
        <Button data-testid="create-website-button" onClick={() => navigate("/partner/websites/new")} className="rounded-full font-bold active:scale-95 transition-transform">
          <PlusCircle className="h-4 w-4 mr-2" /> Create New Website
        </Button>
      </PageHeader>

      <div className="relative max-w-sm mb-8">
        <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid="website-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search websites…" className="pl-10 h-11 rounded-full" />
      </div>

      {!sites ? <LoadingRows /> : sites.length === 0 ? (
        <EmptyState title="No websites yet" body="Launch your first client website with the 5-step builder — it takes under two minutes.">
          <Button data-testid="empty-create-button" onClick={() => navigate("/partner/websites/new")} className="rounded-full font-bold">
            <PlusCircle className="h-4 w-4 mr-2" /> Create Website
          </Button>
        </EmptyState>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {sites.map((w, i) => (
            <motion.div key={w.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              data-testid={`website-card-${w.id}`}
              className="bg-card border border-border rounded-md p-6 hover:shadow-xl hover:shadow-black/5 dark:hover:shadow-black/40 transition-shadow cursor-pointer group"
              onClick={() => navigate(`/partner/websites/${w.id}`)}>
              <div className="flex items-start justify-between">
                <div className="h-11 w-11 rounded-md flex items-center justify-center font-display font-extrabold text-lg"
                  style={{ backgroundColor: (w.settings?.colors?.primary || "#111") + "18", color: w.settings?.colors?.primary || "#888" }}>
                  {w.name[0]}
                </div>
                <StatusBadge status={w.status} />
              </div>
              <p className="font-display font-bold text-lg mt-4 group-hover:underline underline-offset-4">{w.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{w.type.replace("-", " ")} · {w.owner_email}</p>
              <div className="flex items-center gap-4 mt-5 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5"><Package className="h-3.5 w-3.5" />{w.products_count}</span>
                <span className="flex items-center gap-1.5"><ShoppingCart className="h-3.5 w-3.5" />{w.orders_count}</span>
                <a data-testid={`view-store-${w.id}`} href={`/site/${w.slug}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                  className="ml-auto flex items-center gap-1 font-bold text-foreground hover:underline">
                  View store <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
